import { useMutation } from '@apollo/client';
import * as Sentry from '@sentry/browser';
import gql from 'graphql-tag';
import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useSelector, useDispatch } from '../../../../Hooks/index';
import OrderDisplay from '../../Scenes/OrderDisplay';
import * as actions from '~actions';
import { createOrderEasyId, createOrderEasyIdVariables } from './__queries__';
import { CartItem } from '~Types';

const CREATE_ID_MUTATION = gql`
  mutation createOrderEasyId($venueId: String!) {
    createOrderEasyId(venueId: $venueId) {
      orderEasyId
      success
    }
  }
`;

const Wrapper = styled.div``;

interface Props {
  venueId: string;
  orderId: string;
  visible: boolean;
  cartItems: CartItem[];
  reset: () => void;
  setOrderEasyId?: (orderEasyId: string) => void;
  orderEasyId?: string | null;
  showOrderNumber: boolean;
}

const OrderIdManager: React.FunctionComponent<Props> = ({
  venueId,
  orderId,
  visible,
  cartItems,
  reset,
  setOrderEasyId,
  orderEasyId,
  showOrderNumber,
}) => {
  const bongPrintingHalted = useSelector((state) => state.BongPrintingHalted);
  const dispatch = useDispatch();
  const order = useSelector((state) => state.POSOrders.find((o) => o.id === orderId));
  const payment = useSelector((state) => state.POSPayments.find((p) => p.order === orderId));
  const splitOrder = useSelector((state) => state.POSSplitOrder);
  const [createOrderEasyId, { data }] = useMutation<createOrderEasyId, createOrderEasyIdVariables>(
    CREATE_ID_MUTATION,
  );
  const [doneTimeout, setDoneTimeout] = useState(false);
  const [show, setShow] = useState(false);
  const instantOrder = false; // cartItems.every((item) => Boolean(item.instant));

  // Creates and sets orderNumber for order, or sets it to empty string to move on with the order
  useEffect(() => {
    if (order?.orderNumber) {
      return;
    }

    /*if (instantOrder) {
      dispatch(actions.updateOrder(orderId, { orderNumber: '' }));
      return;
    } */

    createOrderEasyId({
      variables: {
        venueId: venueId,
      },
      fetchPolicy: 'no-cache',
    })
      .then(({ data }) => {
        let orderNumber;

        // Is collecting items on order
        if (bongPrintingHalted) {
          if (orderEasyId) {
            // Use "old" easyId
            orderNumber = orderEasyId;
          } else {
            // Create a new easyId
            orderNumber = data?.createOrderEasyId.orderEasyId;
            setOrderEasyId && orderNumber && setOrderEasyId(orderNumber);
          }
        } else {
          if (orderEasyId) {
            orderNumber = orderEasyId;
          } else {
            // Create a new easyId
            orderNumber = data?.createOrderEasyId.orderEasyId;
          }
        }

        if (orderNumber) {
          dispatch(actions.updateOrder(orderId, { orderNumber }));
        } else {
          dispatch(actions.updateOrder(orderId, { orderNumber: '' }));
        }
      })
      .catch((e) => {
        Sentry.captureException(e);
        dispatch(actions.updateOrder(orderId, { orderNumber: '' }));
      });
  }, [instantOrder, order?.orderNumber]);

  useEffect(() => {
    if (payment && visible) {
      setShow(true);
    }
    const resetScreen = () => {
      if (payment) {
        dispatch({
          type: 'REMOVE_POS_PAYMENT',
          id: payment.id,
        });
      }
      dispatch({
        type: 'REMOVE_POS_ORDER',
        id: orderId,
      });
      reset();
    };

    if (doneTimeout) {
      resetScreen();
      // visible = false;
    }

    if (payment?.status === 'COMPLETED' && !splitOrder) {
      const id = setTimeout(() => {
        resetScreen();
      }, 10000);
      return () => clearTimeout(id);
    }

    return () => {};
  }, [payment?.status, doneTimeout]);

  const content = () => {
    /* if (instantOrder) {
      return <InstantOrder done={() => setDoneTimeout(true)} />;
    } */
    return (
      <OrderDisplay
        showOrderNumber={showOrderNumber}
        done={() => setDoneTimeout(true)}
        orderNumber={data && data.createOrderEasyId.orderEasyId}
      />
    );
  };

  return show && showOrderNumber ? <Wrapper>{content()}</Wrapper> : null;
};

export default OrderIdManager;
